import { ArrowRightIcon, BookmarkIcon, CheckCircleIcon, PlusIcon, SparklesIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { TranslationTemplate } from '@/utils/videoTranslationConfig';
import { engineName } from '@/utils/videoTranslationConfig';

interface TemplateGateProps {
  builtIn: TranslationTemplate[];
  saved: TranslationTemplate[];
  onUse: (template: TranslationTemplate) => void;
  onRemove: (id: string) => void;
  onScratch: () => void;
  onCancel?: () => void;
  /** Name of a video already chosen before reaching this gate (e.g. via the Dashboard or Videos page) — carried through into whichever path the user picks below. */
  preselectedVideoName?: string;
}

export function TemplateGate({ builtIn, saved, onUse, onRemove, onScratch, onCancel, preselectedVideoName }: TemplateGateProps) {
  const groups = [
    { title: 'Suggested templates', items: builtIn },
    { title: 'Your saved templates', items: saved },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Start from a template
        </h2>
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
          Reuse a set of engines and voice settings — drop in your video and submit. Or configure
          everything step by step.
        </p>
        {preselectedVideoName && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-green-50 px-3 py-1.5 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <CheckCircleIcon className="h-4 w-4 shrink-0" />
            <span>
              Video ready: <span className="font-medium">{preselectedVideoName}</span> — pick a template or start from scratch to continue with it.
            </span>
          </div>
        )}
      </div>

      {groups.map((group) => (
        <div key={group.title} className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {group.title}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {group.items.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => onUse(template)}
                className="group relative rounded-xl border border-gray-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-500"
              >
                <div className="flex items-start gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                    {template.builtIn ? (
                      <SparklesIcon className="h-4 w-4" />
                    ) : (
                      <BookmarkIcon className="h-4 w-4" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{template.name}</h3>
                    <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                      {template.description}
                    </p>
                  </div>
                  {!template.builtIn && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Delete ${template.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(template.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          onRemove(template.id);
                        }
                      }}
                      className="rounded-md p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-gray-700"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {[
                    template.config.target,
                    engineName(template.config.stt, 'stt'),
                    engineName(template.config.mt, 'mt'),
                    engineName(template.config.tts, 'tts'),
                  ].map((chip) => (
                    <span
                      key={chip}
                      className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                    >
                      {chip}
                    </span>
                  ))}
                </div>

                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400">
                  Use template <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
        <div className="flex-1 text-sm text-gray-500 dark:text-gray-400">
          Prefer full control? Walk through all the steps.
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={onScratch}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <PlusIcon className="h-4 w-4" /> Start from scratch
        </button>
      </div>
    </div>
  );
}
